<script setup lang="ts">
import { useRouter, type RouteLocationRaw } from 'vue-router'

import AppIcon from '@/components/AppIcon.vue'
import type { AppIconName } from '@/components/AppIcon.vue'
import AppButton from '@/components/ui/AppButton.vue'
import { navigateBackFromSubPage } from '@/router/returnNavigation'
import { useAuthStore } from '@/stores/auth'

// 子页面标准导航栏(返回 + 标题 + 可选操作区);标题由 AppShell 从 route.meta.title 解析后传入。
const props = defineProps<{
  title: string
  actionLabel?: string | undefined
  actionTo?: RouteLocationRaw | undefined
  actionIcon?: AppIconName | undefined
  actionDisabled?: boolean | undefined
  backTo?: string | undefined
}>()

defineSlots<{
  actions?: () => unknown
}>()
defineEmits<{ action: [] }>()

const router = useRouter()
const auth = useAuthStore()

async function goBack(): Promise<void> {
  await navigateBackFromSubPage(
    router,
    props.backTo,
    auth.canReceive && !auth.canShip ? '/receiving' : '/customer',
  )
}
</script>

<template>
  <header class="sub-page-header">
    <AppButton
      class="sub-page-header__back"
      variant="text"
      type="button"
      size="medium"
      :aria-label="$t('common.back')"
      @click="goBack"
    >
      <AppIcon name="chevron-left" size="22px" />
    </AppButton>
    <div class="sub-page-header__title">{{ title }}</div>
    <div class="sub-page-header__actions">
      <slot name="actions">
        <RouterLink v-if="actionLabel && actionTo" class="sub-page-header__action" :to="actionTo">
          <AppIcon v-if="actionIcon" :name="actionIcon" size="17px" />
          <span>{{ actionLabel }}</span>
        </RouterLink>
        <AppButton
          v-else-if="actionLabel"
          class="sub-page-header__action"
          :disabled="actionDisabled"
          type="button"
          variant="text"
          @click="$emit('action')"
        >
          <AppIcon v-if="actionIcon" :name="actionIcon" size="17px" />
          <span>{{ actionLabel }}</span>
        </AppButton>
      </slot>
    </div>
  </header>
</template>

<style scoped>
.sub-page-header {
  position: sticky;
  z-index: 20;
  top: 0;
  display: grid;
  grid-template-columns: minmax(72px, 1fr) minmax(0, 2fr) minmax(72px, 1fr);
  align-items: center;
  min-height: calc(52px + env(safe-area-inset-top));
  border-bottom: 1px solid var(--color-neutral-100);
  background: rgb(255 253 252 / 94%);
  padding-top: env(safe-area-inset-top);
  backdrop-filter: blur(18px);
}

.sub-page-header__back.t-button {
  display: grid;
  width: 44px;
  min-width: 44px;
  max-width: 44px;
  min-height: 44px;
  justify-self: start;
  place-content: center;
  padding: 0;
  color: var(--color-text);
}

.sub-page-header__title {
  overflow: hidden;
  margin: 0;
  font-size: 16px;
  font-weight: 650;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sub-page-header__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.sub-page-header__action {
  display: inline-flex;
  min-height: 44px;
  align-items: center;
  gap: 4px;
  padding: 0 12px 0 4px;
  color: var(--color-brand-blue-700);
  font-size: 12px;
  font-weight: 700;
  text-decoration: none;
  white-space: nowrap;
}

.sub-page-header__action.t-button {
  min-height: 44px;
  border: 0;
  border-radius: 0;
  background: transparent;
  padding: 0 12px 0 4px;
  box-shadow: none;
  color: var(--color-brand-blue-700);
  font-size: 12px;
  opacity: 1;
}

.sub-page-header__action.t-button:disabled,
.sub-page-header__action.t-button[aria-disabled='true'] {
  background: transparent;
  color: var(--color-text-muted);
  opacity: 1;
}
</style>
